/**
 * EvidenceTracker — Evidence lifecycle management component
 *
 * Displays:
 *  1. A formatted Evidence Collection Plan (parsed from the latest evidence_plan session)
 *  2. Evidence items with their lifecycle status
 *     (recommended → accepted → collecting → collected → analyzed | rejected)
 *  3. Controls for status transitions and adding manual evidence items
 */

import React, { useState, useCallback, useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { WorkspaceResponse } from "../../api/workspaces";
import {
  updateEvidenceItem,
  createEvidenceItem,
  reprocessEvidencePlan,
  EVIDENCE_TYPES,
} from "../../api/workspaceAi";
import type {
  AiSessionResponse,
  EvidenceItemResponse,
  EvidenceItemStatus,
  SourceStatus,
} from "../../api/workspaceAi";
import EvidencePlanDetailModal from "./EvidencePlanDetailModal";
import type { LinkStatus } from "./EvidencePlanDetailModal";
import styles from "./EvidenceTracker.module.css";

interface Props {
  workspace: WorkspaceResponse;
  evidenceItems: EvidenceItemResponse[];
  sessions: AiSessionResponse[];
  onRefresh: () => void;
  /** Which view to render: "plan" shows the Collection Plan, "evidence" shows evidence items.
   *  Defaults to rendering both (legacy behaviour). */
  view?: "plan" | "evidence";
}

/** Lifecycle order for status transitions */
const STATUS_ORDER: EvidenceItemStatus[] = [
  "recommended",
  "accepted",
  "collecting",
  "collected",
  "analyzed",
];

// ── Evidence Plan JSON parser ────────────────────────────────

interface EvidencePlanItem {
  title: string;
  evidence_type: string;
  description?: string;
  collection_guidance?: string;
  priority?: number;
  hypothesis_link?: string;
  estimated_effort?: string;
  source_url?: string;
  source_name?: string;
}

interface CollectionStrategy {
  recommended_order?: string;
  parallel_tracks?: string[];
  dependencies?: string;
}

interface ParsedEvidencePlan {
  evidence_items: EvidencePlanItem[];
  collection_strategy?: CollectionStrategy;
  plan_summary?: string;
}

/**
 * Sanitize AI-generated URLs:
 *  - Strip markdown link syntax: [label](url) → url
 *  - Ensure absolute: add https:// if missing protocol
 *  - Trim whitespace
 */
function sanitizeUrl(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  let url = raw.trim();

  // Handle markdown-style links: [label](url) or bare [url](url)
  const mdMatch = url.match(/\[.*?\]\((https?:\/\/[^)]+)\)/);
  if (mdMatch) return mdMatch[1];

  // Handle bracket-wrapped: [https://example.com]
  const bracketMatch = url.match(/^\[(https?:\/\/[^\]]+)\]$/);
  if (bracketMatch) return bracketMatch[1];

  // If no protocol, add https://
  if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
    url = `https://${url}`;
  }

  return url;
}

/** Clean source_url on each evidence item */
function cleanEvidenceItems(items: EvidencePlanItem[]): EvidencePlanItem[] {
  return items.map((item) => ({
    ...item,
    source_url: sanitizeUrl(item.source_url),
  }));
}

/**
 * Recursively search a parsed JSON value for an `evidence_items` array.
 * AI responses may nest evidence_items at any depth alongside other
 * keys like summary, hypotheses, severity_indicators, etc.
 */
function findEvidenceItems(obj: any, depth = 0): EvidencePlanItem[] | null {
  if (!obj || typeof obj !== "object" || depth > 5) return null;

  // Direct hit — AI may use different key names for the same concept
  const candidateKeys = ["evidence_items", "evidence_recommendations", "evidenceItems", "evidence", "items"];
  for (const key of candidateKeys) {
    if (Array.isArray(obj[key]) && obj[key].length > 0) {
      return cleanEvidenceItems(obj[key]);
    }
  }

  // Recurse into object values (not arrays — arrays of primitives aren't useful)
  for (const val of Object.values(obj)) {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const found = findEvidenceItems(val, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

/** Extract plan_summary string from anywhere in a parsed JSON object */
const PLAN_SUMMARY_KEYS = ["plan_summary", "planSummary", "executive_summary", "summary"];

function findPlanSummary(obj: any, depth = 0): string | undefined {
  if (!obj || typeof obj !== "object" || depth > 5) return undefined;
  for (const key of PLAN_SUMMARY_KEYS) {
    const val = obj[key];
    if (typeof val === "string" && val.trim().length > 20) {
      return val.trim();
    }
  }
  for (const val of Object.values(obj)) {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const found = findPlanSummary(val, depth + 1);
      if (found) return found;
    }
  }
  return undefined;
}

/** Extract collection_strategy from anywhere in a parsed JSON object */
function findCollectionStrategy(obj: any, depth = 0): CollectionStrategy | undefined {
  if (!obj || typeof obj !== "object" || depth > 5) return undefined;
  if (obj.collection_strategy && typeof obj.collection_strategy === "object") {
    return obj.collection_strategy;
  }
  for (const val of Object.values(obj)) {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const found = findCollectionStrategy(val, depth + 1);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Try to parse all JSON blocks from text (may contain prose around JSON).
 * Returns all successfully parsed JSON objects.
 */
function extractJsonObjects(text: string): any[] {
  const results: any[] = [];

  // Strategy 1: the entire text is JSON
  try {
    const parsed = JSON.parse(text.trim());
    if (typeof parsed === "object") results.push(parsed);
    return results; // whole thing parsed — no need to scan
  } catch {
    // fall through to scanning
  }

  // Strategy 2: strip markdown code fences
  const stripped = text.trim();
  if (stripped.startsWith("```")) {
    const firstNewline = stripped.indexOf("\n");
    if (firstNewline > 0 && stripped.endsWith("```")) {
      const inner = stripped.substring(firstNewline + 1, stripped.length - 3);
      try {
        const parsed = JSON.parse(inner.trim());
        if (typeof parsed === "object") results.push(parsed);
        return results;
      } catch {
        // fall through
      }
    }
  }

  // Strategy 3: bracket-depth scan for JSON objects
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== "{") continue;
    let depth = 0;
    let inStr = false;
    let esc = false;
    let end = -1;
    for (let j = i; j < text.length; j++) {
      const c = text[j];
      if (esc) { esc = false; continue; }
      if (c === "\\" && inStr) { esc = true; continue; }
      if (c === '"') { inStr = !inStr; continue; }
      if (inStr) continue;
      if (c === "{") depth++;
      else if (c === "}") {
        depth--;
        if (depth === 0) { end = j; break; }
      }
    }
    if (end < 0) continue;
    try {
      const parsed = JSON.parse(text.substring(i, end + 1));
      if (typeof parsed === "object") {
        results.push(parsed);
        i = end; // skip past this JSON block
      }
    } catch {
      // not valid JSON at this position
    }
  }

  return results;
}

function tryParseEvidencePlan(
  session: AiSessionResponse
): ParsedEvidencePlan | null {
  const sources = [session.outputPayload, session.rawOutput].filter(Boolean) as string[];

  for (const source of sources) {
    const jsonObjects = extractJsonObjects(source);

    for (const obj of jsonObjects) {
      const items = findEvidenceItems(obj);
      if (items && items.length > 0) {
        return {
          evidence_items: items,
          collection_strategy: findCollectionStrategy(obj),
          plan_summary: findPlanSummary(obj),
        };
      }
    }
  }

  return null;
}

// ── Component ────────────────────────────────────────────────

const EvidenceTracker: React.FC<Props> = ({
  workspace,
  evidenceItems,
  sessions,
  onRefresh,
  view,
}) => {
  const { t } = useAppTranslation("copilot");
  const [addingNew, setAddingNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState(EVIDENCE_TYPES[0]);
  const [newDescription, setNewDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [planExpanded, setPlanExpanded] = useState(true);
  const [selectedPlanItem, setSelectedPlanItem] = useState<EvidencePlanItem | null>(null);

  // Find the latest evidence_plan session that contains parseable evidence_items.
  // Try all completed evidence_plan sessions (most recent first) since older
  // ones may have been submitted with the wrong AI response format.
  const { evidencePlanSession, parsedPlan } = useMemo(() => {
    const candidates = sessions.filter(
      (s) =>
        s.sessionType === "evidence_plan" &&
        (s.status === "completed" || s.status === "reviewed")
    );
    for (const candidate of candidates) {
      const plan = tryParseEvidencePlan(candidate);
      if (plan) {
        return { evidencePlanSession: candidate, parsedPlan: plan };
      }
    }
    // No parseable plan found — return first candidate (if any) for the reprocess button
    return {
      evidencePlanSession: candidates[0] ?? null,
      parsedPlan: null as ParsedEvidencePlan | null,
    };
  }, [sessions]);

  // Determine if reprocess is needed (plan session exists but no evidence items from it)
  const needsReprocess = useMemo(() => {
    if (!evidencePlanSession || !parsedPlan) return false;
    const fromThisSession = evidenceItems.filter(
      (e) => e.recommendedBySession === evidencePlanSession.sessionGuid
    );
    return fromThisSession.length === 0 && parsedPlan.evidence_items.length > 0;
  }, [evidencePlanSession, parsedPlan, evidenceItems]);

  // Group by status
  const groupedByStatus = evidenceItems.reduce<
    Record<string, EvidenceItemResponse[]>
  >((acc, item) => {
    if (!acc[item.status]) acc[item.status] = [];
    acc[item.status].push(item);
    return acc;
  }, {});

  const handleStatusChange = useCallback(
    async (item: EvidenceItemResponse, newStatus: EvidenceItemStatus) => {
      try {
        await updateEvidenceItem(
          workspace.workspaceGuid,
          item.evidenceGuid,
          { status: newStatus }
        );
        onRefresh();
      } catch {
        // error handling
      }
    },
    [workspace.workspaceGuid, onRefresh]
  );

  const handleAddEvidence = useCallback(async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      await createEvidenceItem(workspace.workspaceGuid, {
        evidenceType: newType,
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
      });
      setNewTitle("");
      setNewDescription("");
      setAddingNew(false);
      onRefresh();
    } catch {
      // error handling
    } finally {
      setSaving(false);
    }
  }, [workspace.workspaceGuid, newTitle, newType, newDescription, onRefresh]);

  const handleReprocess = useCallback(async () => {
    if (!evidencePlanSession) return;
    setReprocessing(true);
    try {
      await reprocessEvidencePlan(
        workspace.workspaceGuid,
        evidencePlanSession.sessionGuid
      );
      onRefresh();
    } catch {
      // error handling
    } finally {
      setReprocessing(false);
    }
  }, [workspace.workspaceGuid, evidencePlanSession, onRefresh]);

  /**
   * Build a lookup from plan-item title → matching DB evidence item.
   * This allows the detail modal to read/write sourceStatus via the API
   * even though plan items are parsed from ephemeral AI JSON.
   */
  const planItemToEvidence = useMemo(() => {
    const map = new Map<string, EvidenceItemResponse>();
    for (const dbItem of evidenceItems) {
      const key = dbItem.title.trim().toLowerCase();
      if (!map.has(key)) map.set(key, dbItem);
    }
    return map;
  }, [evidenceItems]);

  /** Find the DB evidence item that matches a plan item (by title) */
  const findMatchingEvidence = useCallback(
    (planItem: EvidencePlanItem): EvidenceItemResponse | undefined => {
      return planItemToEvidence.get(planItem.title.trim().toLowerCase());
    },
    [planItemToEvidence]
  );

  /** Persist link status to the DB via the API */
  const handleLinkStatusChange = useCallback(
    async (sourceUrl: string, status: LinkStatus) => {
      if (!selectedPlanItem) return;
      const match = findMatchingEvidence(selectedPlanItem);
      if (!match) return; // No DB record yet — nothing to persist
      try {
        await updateEvidenceItem(workspace.workspaceGuid, match.evidenceGuid, {
          sourceStatus: status as SourceStatus,
        });
        onRefresh();
      } catch {
        // silent — local UI already updated
      }
    },
    [selectedPlanItem, findMatchingEvidence, workspace.workspaceGuid, onRefresh]
  );

  const nextStatus = (
    current: EvidenceItemStatus
  ): EvidenceItemStatus | null => {
    const idx = STATUS_ORDER.indexOf(current);
    if (idx < 0 || idx >= STATUS_ORDER.length - 1) return null;
    return STATUS_ORDER[idx + 1];
  };

  const effortLabel = (effort?: string): string => {
    if (!effort) return "";
    const map: Record<string, string> = {
      quick: "⚡ Quick",
      moderate: "⏱ Moderate",
      deep: "🔬 Deep",
    };
    return map[effort] ?? effort;
  };

  const priorityLabel = (priority?: number): string => {
    if (!priority) return "";
    const labels = ["", "Low", "Low–Med", "Medium", "Med–High", "High"];
    return labels[priority] ?? `P${priority}`;
  };

  const showPlan = !view || view === "plan";
  const showEvidence = !view || view === "evidence";

  return (
    <div className={styles.tracker}>
      {/* ── Evidence Collection Plan ── */}
      {showPlan && parsedPlan && (
        view === "plan" ? (
          /* Plan-only tab: render table directly without section wrapper */
          <div className={styles.planBody}>
            <div className={styles.planTable}>
              <div className={styles.planTableHeader}>
                <span className={styles.planColPriority}>
                  {t("plan.colPriority")}
                </span>
                <span className={styles.planColTitle}>
                  {t("plan.colEvidence")}
                </span>
                <span className={styles.planColType}>
                  {t("plan.colType")}
                </span>
                <span className={styles.planColEffort}>
                  {t("plan.colEffort")}
                </span>
              </div>
              {parsedPlan.evidence_items.map((item, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={styles.planTableRow}
                  onClick={() => setSelectedPlanItem(item)}
                >
                  <span className={styles.planColPriority}>
                    <span className={styles.priorityBadge}>
                      {priorityLabel(item.priority)}
                    </span>
                  </span>
                  <span className={styles.planColTitle}>
                    <span className={styles.planItemTitle}>{item.title}</span>
                  </span>
                  <span className={styles.planColType}>
                    {t(`evidenceTypesShort.${item.evidence_type}`, { defaultValue: "" }) ||
                      t(`evidenceTypes.${item.evidence_type}`) ||
                      item.evidence_type}
                  </span>
                  <span className={styles.planColEffort}>
                    {effortLabel(item.estimated_effort)}
                  </span>
                </button>
              ))}
            </div>

            {needsReprocess && (
              <button
                type="button"
                className={styles.reprocessBtn}
                onClick={handleReprocess}
                disabled={reprocessing}
              >
                {reprocessing
                  ? t("plan.reprocessing")
                  : t("plan.reprocess")}
              </button>
            )}
          </div>
        ) : (
          /* Full view: collapsible section with header */
          <div className={styles.planSection}>
            <button
              type="button"
              className={styles.planHeader}
              onClick={() => setPlanExpanded((p) => !p)}
            >
              <span className={styles.planHeaderIcon}>
                {planExpanded ? "▾" : "▸"}
              </span>
              <span className={styles.planHeaderTitle}>
                {t("plan.title")}
              </span>
              <span className={styles.planHeaderCount}>
                {t("plan.itemCount", {
                  count: parsedPlan.evidence_items.length,
                })}
              </span>
            </button>

            {planExpanded && (
              <div className={styles.planBody}>
                <div className={styles.planTable}>
                  <div className={styles.planTableHeader}>
                    <span className={styles.planColPriority}>
                      {t("plan.colPriority")}
                    </span>
                    <span className={styles.planColTitle}>
                      {t("plan.colEvidence")}
                    </span>
                    <span className={styles.planColType}>
                      {t("plan.colType")}
                    </span>
                    <span className={styles.planColEffort}>
                      {t("plan.colEffort")}
                    </span>
                  </div>
                  {parsedPlan.evidence_items.map((item, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={styles.planTableRow}
                      onClick={() => setSelectedPlanItem(item)}
                    >
                      <span className={styles.planColPriority}>
                        <span className={styles.priorityBadge}>
                          {priorityLabel(item.priority)}
                        </span>
                      </span>
                      <span className={styles.planColTitle}>
                        <span className={styles.planItemTitle}>{item.title}</span>
                      </span>
                      <span className={styles.planColType}>
                        {t(`evidenceTypes.${item.evidence_type}`) ||
                          item.evidence_type}
                      </span>
                      <span className={styles.planColEffort}>
                        {effortLabel(item.estimated_effort)}
                      </span>
                    </button>
                  ))}
                </div>

                {needsReprocess && (
                  <button
                    type="button"
                    className={styles.reprocessBtn}
                    onClick={handleReprocess}
                    disabled={reprocessing}
                  >
                    {reprocessing
                      ? t("plan.reprocessing")
                      : t("plan.reprocess")}
                  </button>
                )}
              </div>
            )}
          </div>
        )
      )}

      {/* ── Plan-only empty state ── */}
      {showPlan && !parsedPlan && (
        <div className={styles.emptyState}>
          <span>{t("plan.empty")}</span>
        </div>
      )}

      {/* ── Summary bar ── */}
      {showEvidence && evidenceItems.length > 0 && (
        <div className={styles.summaryBar}>
          {STATUS_ORDER.map((status) => {
            const count = groupedByStatus[status]?.length ?? 0;
            return (
              <div
                key={status}
                className={`${styles.summaryChip} ${
                  count > 0 ? styles[`chip_${status}`] : styles.chipEmpty
                }`}
              >
                <span className={styles.chipCount}>{count}</span>
                <span className={styles.chipLabel}>
                  {t(`evidenceStatus.${status}`)}
                </span>
              </div>
            );
          })}
          {(groupedByStatus["rejected"]?.length ?? 0) > 0 && (
            <div className={`${styles.summaryChip} ${styles.chip_rejected}`}>
              <span className={styles.chipCount}>
                {groupedByStatus["rejected"]?.length ?? 0}
              </span>
              <span className={styles.chipLabel}>
                {t("evidenceStatus.rejected")}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Evidence list ── */}
      {showEvidence && evidenceItems.length === 0 && !addingNew ? (
        <div className={styles.emptyState}>
          <span>{t("evidenceEmpty")}</span>
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => setAddingNew(true)}
          >
            {t("addEvidence")}
          </button>
        </div>
      ) : evidenceItems.length > 0 ? (
        <>
          <div className={styles.evidenceList}>
            {evidenceItems.map((item) => {
              const next = nextStatus(item.status as EvidenceItemStatus);
              return (
                <div key={item.evidenceGuid} className={styles.evidenceCard}>
                  <div className={styles.evidenceTop}>
                    <span className={styles.evidenceType}>
                      {t(
                        `evidenceTypes.${item.evidenceType}`
                      )}
                    </span>
                    <span
                      className={`${styles.evidenceStatusBadge} ${
                        styles[`badge_${item.status}`] ?? ""
                      }`}
                    >
                      {t(`evidenceStatus.${item.status}`)}
                    </span>
                  </div>
                  <div className={styles.evidenceTitle}>{item.title}</div>
                  {item.description && (
                    <div className={styles.evidenceDesc}>
                      {item.description}
                    </div>
                  )}
                  {item.collectionGuidance && (() => {
                    /* Split on newlines first; if that yields only one chunk,
                       fall back to splitting on "N. " patterns (the AI sometimes
                       returns all steps on a single line). */
                    let rawSteps = item.collectionGuidance.split("\n").filter((s) => s.trim());
                    if (rawSteps.length <= 1) {
                      rawSteps = item.collectionGuidance
                        .split(/(?=\d+\.\s)/)
                        .filter((s) => s.trim());
                    }
                    const steps = rawSteps.map((s) =>
                      s.replace(/^\d+[.)]\s*/, "").trim()
                    ).filter(Boolean);

                    return (
                      <div className={styles.guidanceBlock}>
                        <span className={styles.guidanceLabel}>
                          {t("collectionGuidance")}
                        </span>
                        {steps.length > 1 ? (
                          <ol className={styles.guidanceList}>
                            {steps.map((step, i) => (
                              <li key={i} className={styles.guidanceStep}>{step}</li>
                            ))}
                          </ol>
                        ) : (
                          <span className={styles.guidanceText}>
                            {item.collectionGuidance}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <div className={styles.evidenceActions}>
                    {next && item.status !== "rejected" && (
                      <button
                        type="button"
                        className={styles.advanceBtn}
                        onClick={() =>
                          handleStatusChange(
                            item,
                            next
                          )
                        }
                      >
                        {t(`evidenceAction.${next}`)}
                      </button>
                    )}
                    {item.status !== "rejected" &&
                      item.status !== "analyzed" && (
                        <button
                          type="button"
                          className={styles.rejectBtn}
                          onClick={() =>
                            handleStatusChange(item, "rejected")
                          }
                        >
                          {t("evidenceAction.reject")}
                        </button>
                      )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add evidence button */}
          {!addingNew && (
            <button
              type="button"
              className={styles.addBtn}
              onClick={() => setAddingNew(true)}
            >
              {t("addEvidence")}
            </button>
          )}
        </>
      ) : null}

      {/* ── Evidence Plan Detail Modal ── */}
      {selectedPlanItem && (
        <EvidencePlanDetailModal
          item={selectedPlanItem}
          onClose={() => setSelectedPlanItem(null)}
          onLinkStatusChange={handleLinkStatusChange}
          linkStatus={
            (findMatchingEvidence(selectedPlanItem)?.sourceStatus as LinkStatus) ?? "unknown"
          }
        />
      )}

      {/* ── Add new evidence form ── */}
      {showEvidence && addingNew && (
        <div className={styles.addForm}>
          <div className={styles.addFormRow}>
            <div className={styles.addField}>
              <label className={styles.addLabel}>
                {t("evidenceTypeLabel")}
              </label>
              <select
                className={styles.addSelect}
                value={newType}
                onChange={(e) => setNewType(e.target.value as any)}
              >
                {EVIDENCE_TYPES.map((et) => (
                  <option key={et} value={et}>
                    {t(`evidenceTypes.${et}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.addField} style={{ flex: 1 }}>
              <label className={styles.addLabel}>
                {t("evidenceTitleLabel")}
              </label>
              <input
                type="text"
                className={styles.addInput}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={t("evidenceTitlePlaceholder")}
                disabled={saving}
              />
            </div>
          </div>
          <div className={styles.addField}>
            <label className={styles.addLabel}>
              {t("evidenceDescLabel")}
            </label>
            <textarea
              className={styles.addTextarea}
              rows={2}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder={t("evidenceDescPlaceholder")}
              disabled={saving}
            />
          </div>
          <div className={styles.addActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => {
                setAddingNew(false);
                setNewTitle("");
                setNewDescription("");
              }}
              disabled={saving}
            >
              {t("wizard.back")}
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleAddEvidence}
              disabled={!newTitle.trim() || saving}
            >
              {saving
                ? t("saving")
                : t("saveEvidence")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvidenceTracker;
